import { pxToEm } from 'util/pxToEm';
import { Box, Typography, Button } from '@mui/material';
import { Image } from 'components/lib';
import React, { ReactElement } from 'react';
import { OrganizationStatus, OrganizationSummary } from 'services/api';
import { Role, RoleMap } from 'hooks/useGetRole';

export interface OrganizationCardProps {
  organization: OrganizationSummary;
  onRequestAdminAccess?: (organizationId: string) => void;
  role: Role[] | undefined;
}

export const OrganizationCard = ({
  organization,
  onRequestAdminAccess,
  role,
}: OrganizationCardProps): ReactElement => {
  const handleRequestAdminAccess = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onRequestAdminAccess) onRequestAdminAccess(organization.id);
  };

  const isAdmin = role && [RoleMap.admin].some(r => role.includes(r));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #DEDEDE',
        borderRadius: '8px',
        padding: '16px',
        gap: '16px',
        backgroundColor: '#FCFCFF',
        height: '100%',
        minHeight: '120px',
        minWidth: 0,
        width: '100%',
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Image
          src={organization.logoUrl}
          alt={organization.name}
          width={50}
          height={50}
          style={{
            width: 50,
            height: 50,
            borderRadius: '8px',
            border: '1px solid #E8EAEF',
            objectFit: 'cover',
            display: 'block',
            flexShrink: 0,
          }}
        />

        {!organization.isJoined && !organization.isRequested && isAdmin && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleRequestAdminAccess}
            sx={{
              borderColor: '#23305F',
              borderRadius: '6px',
              color: '#23305F',
              fontSize: pxToEm(14),
              fontWeight: 400,
              textTransform: 'none',
              padding: '13px 16px',
              minWidth: 'auto',
              '&:hover': {
                borderColor: '#23305F',
                backgroundColor: 'rgba(35, 48, 95, 0.04)',
              },
            }}
          >
            Request Admin Access
          </Button>
        )}

        {!organization.isJoined && organization.isRequested && isAdmin && (
          <Typography
            sx={{
              fontSize: pxToEm(12),
              color: '#666',
              fontStyle: 'italic',
            }}
          >
            Request Pending
          </Typography>
        )}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: pxToEm(14),
            fontWeight: 500,
            lineHeight: pxToEm(25),
            color: '#383838',
            wordBreak: 'break-word',
          }}
        >
          {organization.name}
        </Typography>
        {organization.status === OrganizationStatus.INACTIVE ? (
          <Typography
            sx={{
              fontSize: pxToEm(12),
              fontWeight: 400,
              lineHeight: pxToEm(18),
              color: '#5F6375',
              mt: '2px',
            }}
          >
            Inactive
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
